import { useState, useCallback, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AdminNav } from '../components/AdminChrome';
import { formatDate } from '../lib/date';
import { stripCommitPrefix } from '../components/ChangelogList';

const TAGS = ['fix', 'feature', 'update'];
const LEVELS = ['ok', 'degraded', 'outage'];

const TAG_STYLES = {
    feature: 'bg-primary/10 text-primary',
    fix: 'bg-rose-50 text-rose-600',
    outage: 'bg-red-50 text-red-700',
    update: 'bg-amber-50 text-amber-700',
};

export default function AdminChangelog() {
    const [password, setPassword] = useState(sessionStorage.getItem('mimique_admin_token') || '');
    const [authed, setAuthed] = useState(!!sessionStorage.getItem('mimique_admin_token'));
    const [entries, setEntries] = useState([]);
    const [status, setStatus] = useState({ level: 'ok', message: '' });
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    // manual entry form
    const [newTag, setNewTag] = useState('fix');
    const [newMessage, setNewMessage] = useState('');
    const [newPublic, setNewPublic] = useState(true);
    const [newDate, setNewDate] = useState('');
    // multi-select
    const [selected, setSelected] = useState(new Set());
    // inline editing: editingId -> { message, tag }
    const [editing, setEditing] = useState({});

    const base = process.env.REACT_APP_BACKEND_URL || '';

    const apiFetch = useCallback((path, opts = {}) => {
        return fetch(`${base}${path}`, {
            ...opts,
            headers: { 'Authorization': `Bearer ${password}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
        });
    }, [base, password]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [eRes, sRes] = await Promise.all([
                apiFetch('/api/admin/changelog'),
                fetch(`${base}/api/status`),
            ]);
            if (eRes.status === 401) { toast.error('Wrong password'); setAuthed(false); return; }
            setEntries(await eRes.json());
            setStatus(await sRes.json());
        } catch {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, base]);

    useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await apiFetch('/api/admin/changelog');
        setLoading(false);
        if (res.status === 401) { toast.error('Wrong password'); return; }
        sessionStorage.setItem('mimique_admin_token', password);
        setAuthed(true);
        setEntries(await res.json());
        fetch(`${base}/api/status`).then(r => r.json()).then(setStatus).catch(() => {});
    };

    // ── selection helpers ──────────────────────────────────────────────────
    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const hidden = entries.filter(e => !e.public);
        if (selected.size === hidden.length && hidden.length > 0) {
            setSelected(new Set());
        } else {
            setSelected(new Set(hidden.map(e => e.id)));
        }
    };

    // ── publish selected (bulk) ────────────────────────────────────────────
    const handlePublishSelected = async () => {
        const ids = [...selected];
        await Promise.all(ids.map(id => apiFetch(`/api/admin/changelog/toggle/${id}`, { method: 'POST' })));
        setEntries(prev => prev.map(e => selected.has(e.id) ? { ...e, public: true } : e));
        setSelected(new Set());
        toast.success(`Published ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'}`);
    };

    // ── single toggle ──────────────────────────────────────────────────────
    const handleToggle = async (id) => {
        const res = await apiFetch(`/api/admin/changelog/toggle/${id}`, { method: 'POST' });
        if (!res.ok) { toast.error('Toggle failed'); return; }
        const updated = await res.json();
        setEntries(prev => prev.map(e => e.id === id ? { ...e, public: updated.public } : e));
        setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    };

    // ── inline edit ────────────────────────────────────────────────────────
    const startEdit = (entry) => {
        setEditing(prev => ({ ...prev, [entry.id]: { message: stripCommitPrefix(entry.message), tag: entry.tag } }));
    };

    const cancelEdit = (id) => {
        setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const handleSaveEdit = async (id, alsoPublish = false) => {
        const { message, tag } = editing[id];
        const patch = { message, tag };
        if (alsoPublish) patch.public = true;
        const res = await apiFetch(`/api/admin/changelog/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        });
        if (!res.ok) { toast.error('Save failed'); return; }
        const updated = await res.json();
        setEntries(prev => prev.map(e => e.id === id ? updated : e));
        cancelEdit(id);
        setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
        toast.success(alsoPublish ? 'Saved & published' : 'Saved');
    };

    // ── delete ─────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this entry?')) return;
        const res = await apiFetch(`/api/admin/changelog/${id}`, { method: 'DELETE' });
        if (!res.ok) { toast.error('Delete failed'); return; }
        setEntries(prev => prev.filter(e => e.id !== id));
        setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
        toast.success('Deleted');
    };

    // ── manual entry ───────────────────────────────────────────────────────
    const handleAddManual = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        const res = await apiFetch('/api/admin/changelog/manual', {
            method: 'POST',
            body: JSON.stringify({
                tag: newTag,
                message: newMessage.trim(),
                public: newPublic,
                commit_date: newDate ? new Date(newDate).toISOString() : undefined,
            }),
        });
        if (!res.ok) { toast.error('Failed to add entry'); return; }
        const entry = await res.json();
        setEntries(prev => [entry, ...prev].sort((a, b) => (b.commit_date || '').localeCompare(a.commit_date || '')));
        setNewMessage('');
        setNewDate('');
        toast.success('Entry added');
    };

    // ── status ─────────────────────────────────────────────────────────────
    const handleSaveStatus = async () => {
        const res = await apiFetch('/api/admin/status', {
            method: 'POST',
            body: JSON.stringify({ level: status.level, message: status.message }),
        });
        if (!res.ok) { toast.error('Failed to save status'); return; }
        toast.success('Status updated');
    };

    // ── sync ───────────────────────────────────────────────────────────────
    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await apiFetch('/api/admin/sync-commits', { method: 'POST' });
            const data = await res.json();
            if (!res.ok) { toast.error(data.detail || 'Sync failed'); return; }
            toast.success(`Synced ${data.synced} new commit(s)`);
            await load();
        } finally {
            setSyncing(false);
        }
    };

    // ── login screen ───────────────────────────────────────────────────────
    if (!authed) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <form onSubmit={handleLogin} className="w-full max-w-xs space-y-4">
                    <h1 className="text-xl font-bold text-foreground">Admin — Changelog</h1>
                    <input
                        type="password"
                        placeholder="Admin password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                    />
                    <Button type="submit" className="w-full rounded-full" disabled={loading}>
                        {loading ? 'Checking…' : 'Log in'}
                    </Button>
                </form>
            </div>
        );
    }

    const hiddenCount = entries.filter(e => !e.public).length;

    return (
        <div className="min-h-screen bg-background px-4 py-10 max-w-3xl mx-auto space-y-10">
            <div><AdminNav /><h1 className="text-xl font-bold text-foreground">Changelog</h1></div>

            {/* Status updater */}
            <section className="space-y-3">
                <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">App Status</h2>
                <div className="flex gap-2 flex-wrap">
                    <select
                        value={status.level}
                        onChange={e => setStatus(s => ({ ...s, level: e.target.value }))}
                        className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50"
                    >
                        {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <input
                        value={status.message || ''}
                        onChange={e => setStatus(s => ({ ...s, message: e.target.value }))}
                        placeholder="Status message"
                        className="flex-1 min-w-48 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Button onClick={handleSaveStatus} className="rounded-full">Save</Button>
                </div>
            </section>

            {/* Sync */}
            <section className="space-y-2">
                <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">GitHub Commits</h2>
                <Button onClick={handleSync} disabled={syncing} variant="outline" className="rounded-full">
                    {syncing ? 'Syncing…' : 'Sync commits'}
                </Button>
            </section>

            {/* Manual entry */}
            <section className="space-y-3">
                <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">Add Manual Entry</h2>
                <form onSubmit={handleAddManual} className="flex gap-2 flex-wrap items-end">
                    <select
                        value={newTag}
                        onChange={e => setNewTag(e.target.value)}
                        className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50"
                    >
                        {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        placeholder="Message"
                        className="flex-1 min-w-48 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <input
                        type="date"
                        value={newDate}
                        onChange={e => setNewDate(e.target.value)}
                        title="Optional — backdate this entry"
                        className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50"
                    />
                    <label className="flex items-center gap-1.5 text-sm text-stone-600 cursor-pointer">
                        <input type="checkbox" checked={newPublic} onChange={e => setNewPublic(e.target.checked)} />
                        Public
                    </label>
                    <Button type="submit" className="rounded-full" disabled={!newMessage.trim()}>Add</Button>
                </form>
            </section>

            {/* Entry list */}
            <section className="space-y-3">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">
                        All Entries ({entries.length})
                    </h2>
                    {/* Bulk actions */}
                    <div className="flex items-center gap-2">
                        {hiddenCount > 0 && (
                            <button
                                onClick={toggleSelectAll}
                                className="text-xs text-stone-500 hover:text-stone-700 underline"
                            >
                                {selected.size === hiddenCount ? 'Deselect all' : `Select all hidden (${hiddenCount})`}
                            </button>
                        )}
                        {selected.size > 0 && (
                            <Button onClick={handlePublishSelected} className="rounded-full h-7 text-xs px-3">
                                Publish selected ({selected.size})
                            </Button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : entries.length === 0 ? (
                    <p className="text-stone-400 text-sm">No entries yet. Sync commits or add manually.</p>
                ) : (
                    <div className="space-y-1">
                        {entries.map(entry => {
                            const isEditing = !!editing[entry.id];
                            const draft = editing[entry.id] || {};
                            const isSelected = selected.has(entry.id);

                            return (
                                <div key={entry.id} className={`rounded-lg border transition-colors ${isSelected ? 'border-primary/30 bg-primary/5' : 'border-stone-100 bg-transparent'}`}>
                                    {/* Meta row */}
                                    <div className="flex items-center gap-3 px-3 pt-2 pb-1">
                                        {!entry.public ? (
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleSelect(entry.id)}
                                                className="shrink-0 accent-primary"
                                            />
                                        ) : (
                                            <span className="w-4 shrink-0" />
                                        )}
                                        <span className="text-xs text-stone-400 w-24 shrink-0">{formatDate(entry.commit_date)}</span>
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize shrink-0 ${TAG_STYLES[entry.tag] || ''}`}>
                                            {entry.tag}
                                        </span>
                                        <span className="flex-1" />
                                        <span className="text-xs text-stone-300 shrink-0">{entry.type === 'commit' ? 'git' : 'manual'}</span>
                                        {!isEditing && (
                                            <button
                                                onClick={() => startEdit(entry)}
                                                className="text-xs text-stone-400 hover:text-stone-600 shrink-0"
                                            >
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleToggle(entry.id)}
                                            className={`text-xs px-2 py-1 rounded-full border shrink-0 ${entry.public ? 'border-primary text-primary' : 'border-stone-300 text-stone-400'}`}
                                        >
                                            {entry.public ? 'Public' : 'Hidden'}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(entry.id)}
                                            className="text-xs text-red-400 hover:text-red-600 shrink-0"
                                        >
                                            Delete
                                        </button>
                                    </div>

                                    {/* Message */}
                                    {!isEditing && (
                                        <p className="px-3 pb-2 text-sm text-foreground leading-snug pl-10">
                                            {stripCommitPrefix(entry.message)}
                                        </p>
                                    )}

                                    {/* Inline edit form */}
                                    {isEditing && (
                                        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-stone-100">
                                            <select
                                                value={draft.tag}
                                                onChange={e => setEditing(prev => ({ ...prev, [entry.id]: { ...prev[entry.id], tag: e.target.value } }))}
                                                className="px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50"
                                            >
                                                {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <textarea
                                                value={draft.message}
                                                onChange={e => setEditing(prev => ({ ...prev, [entry.id]: { ...prev[entry.id], message: e.target.value } }))}
                                                rows={3}
                                                className="w-full px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                                                autoFocus
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    onClick={() => handleSaveEdit(entry.id, false)}
                                                    variant="outline"
                                                    className="rounded-full h-8 text-xs px-3"
                                                    disabled={!draft.message?.trim()}
                                                >
                                                    Save
                                                </Button>
                                                {!entry.public && (
                                                    <Button
                                                        onClick={() => handleSaveEdit(entry.id, true)}
                                                        className="rounded-full h-8 text-xs px-3"
                                                        disabled={!draft.message?.trim()}
                                                    >
                                                        Save & publish
                                                    </Button>
                                                )}
                                                <button
                                                    onClick={() => cancelEdit(entry.id)}
                                                    className="text-xs text-stone-400 hover:text-stone-600 px-1"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
